import React, { useState, useEffect } from 'react';
import {
  Box, Button, Typography, Table, TableHead, TableBody, TableRow,
  TableCell, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Stack, Alert
} from '@mui/material';
import { aiUsageApi } from '../services/aiUsageApi';

export default function AiUsagePricingTab() {
  const [rows, setRows]     = useState([]);
  const [open, setOpen]     = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');
  const [loadError, setLoadError] = useState('');
  const [form, setForm]     = useState({
    model_name: '', tokens_in_usd_per_million: '', tokens_out_usd_per_million: '',
    effective_from: new Date().toISOString().slice(0, 16),
  });

  const load = () => aiUsageApi.getPricing()
    .then(r => { setRows(r.rows); setLoadError(''); })
    .catch(e => {
      console.error(e);
      // 999.1594 — this used to fail silently (.catch(console.error)): the
      // rates table just stayed empty with no indication the fetch failed.
      setLoadError(e?.message || 'Failed to load pricing rates.');
    });
  useEffect(() => { load(); }, []);

  const byModel = rows.reduce((acc, r) => {
    if (!acc[r.model_name]) acc[r.model_name] = [];
    acc[r.model_name].push(r);
    return acc;
  }, {});

  const save = async () => {
    setSaving(true); setError('');
    try {
      await aiUsageApi.addPricingRate({
        ...form,
        tokens_in_usd_per_million: parseFloat(form.tokens_in_usd_per_million),
        tokens_out_usd_per_million: parseFloat(form.tokens_out_usd_per_million),
      });
      setOpen(false);
      setForm({ model_name: '', tokens_in_usd_per_million: '', tokens_out_usd_per_million: '', effective_from: new Date().toISOString().slice(0, 16) });
      load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      {loadError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setLoadError('')}>{loadError}</Alert>}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="subtitle1">
          Rates are point-in-time — adding a new row updates all future cost calculations without changing historical data.
        </Typography>
        <Button variant="contained" onClick={() => setOpen(true)}>Add Rate</Button>
      </Stack>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Model</TableCell>
            <TableCell align="right">In ($/1M tokens)</TableCell>
            <TableCell align="right">Out ($/1M tokens)</TableCell>
            <TableCell>Effective From</TableCell>
            <TableCell>Source</TableCell>
            <TableCell>History</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {Object.entries(byModel).map(([model, modelRows]) => {
            const latest = modelRows[0];
            return (
              <TableRow key={model} hover>
                <TableCell><strong>{model}</strong></TableCell>
                <TableCell align="right">${Number(latest.tokens_in_usd_per_million).toFixed(6)}</TableCell>
                <TableCell align="right">${Number(latest.tokens_out_usd_per_million).toFixed(6)}</TableCell>
                <TableCell>{new Date(latest.effective_from).toLocaleDateString()}</TableCell>
                <TableCell>
                  <Chip label={latest.source} size="small" color={latest.source === 'admin' ? 'warning' : 'default'} />
                </TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">{modelRows.length} rate{modelRows.length !== 1 ? 's' : ''}</Typography>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Pricing Rate</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Model Name" fullWidth value={form.model_name}
              onChange={e => setForm(f => ({ ...f, model_name: e.target.value }))}
              placeholder="e.g. gemini-2.0-flash" />
            <TextField label="Input rate ($/1M tokens)" type="number" fullWidth
              value={form.tokens_in_usd_per_million}
              onChange={e => setForm(f => ({ ...f, tokens_in_usd_per_million: e.target.value }))} />
            <TextField label="Output rate ($/1M tokens)" type="number" fullWidth
              value={form.tokens_out_usd_per_million}
              onChange={e => setForm(f => ({ ...f, tokens_out_usd_per_million: e.target.value }))} />
            <TextField label="Effective From" type="datetime-local" fullWidth
              value={form.effective_from}
              onChange={e => setForm(f => ({ ...f, effective_from: e.target.value }))}
              InputLabelProps={{ shrink: true }} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save Rate'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
